import React from 'react';
import { motion } from 'framer-motion';
import { programData } from '../data/course';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

export default function CommunityPage() {
  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-10 pb-24">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-12"
      >
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight mb-3">
          Community
        </h1>
        <p className="text-base/7 text-gray-500 max-w-2xl">
          Stay connected between sessions. Your cohort is your support system.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* WhatsApp */}
        <motion.div
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          whileHover={{ y: -2 }}
          className="rounded-xl border border-gray-950/[0.06] bg-white p-8 hover:border-gray-950/[0.1] hover:shadow-sm transition-all duration-200"
        >
          <div className="w-10 h-10 rounded-lg bg-gray-950/[0.04] flex items-center justify-center mb-5">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-gray-600">
              <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97Z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-[15px] font-semibold text-gray-900 mb-1.5">
            {programData.community.whatsapp.title}
          </h2>
          <p className="text-[14px] text-gray-500 leading-relaxed mb-6">
            {programData.community.whatsapp.description}
          </p>
          <button className="inline-flex items-center gap-x-2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
            Join group
            <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
              <path fillRule="evenodd" d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z" clipRule="evenodd" />
            </svg>
          </button>
        </motion.div>

        {/* Recordings */}
        <motion.div
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          whileHover={{ y: -2 }}
          className="rounded-xl border border-gray-950/[0.06] bg-white p-8 hover:border-gray-950/[0.1] hover:shadow-sm transition-all duration-200"
        >
          <div className="w-10 h-10 rounded-lg bg-gray-950/[0.04] flex items-center justify-center mb-5">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-gray-600">
              <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm14.024-.983a1.125 1.125 0 0 1 0 1.966l-5.603 3.113A1.125 1.125 0 0 1 9 15.113V8.887c0-.857.921-1.4 1.671-.983l5.603 3.113Z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-[15px] font-semibold text-gray-900 mb-1.5">
            {programData.community.recordings.title}
          </h2>
          <p className="text-[14px] text-gray-500 leading-relaxed mb-6">
            {programData.community.recordings.description}
          </p>

          <div className="space-y-2">
            {programData.weeks.map((week) => (
              <div
                key={week.number}
                className="group flex items-center gap-3 p-3 rounded-lg border border-gray-950/[0.04] bg-gray-950/[0.015] hover:bg-gray-950/[0.03] transition-colors"
              >
                <div className="w-7 h-7 rounded-md bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3 text-gray-400">
                    <path d="M3 2.5a.75.75 0 0 1 1.145-.638l9 5.5a.75.75 0 0 1 0 1.276l-9 5.5A.75.75 0 0 1 3 13.5V2.5Z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    Week {week.number}: {week.liveSession.title}
                  </p>
                </div>
                <span className="text-[11px] text-gray-400">
                  Coming soon
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
